import React, { useEffect, useRef, useState } from "react";
import { StorageUtils } from "../Utils/StorageUtils";
import { useUserProfile } from "../contexts/UserProfileContext";  
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Form
} from "react-bootstrap";
import "../CSS/Dashboard.css";
import "../CSS/Dash.css";
import BarChart from "../components/UI/BarChart.jsx";
import SearchBar, { highlightSearchTerm } from "../components/UI/SearchBar.jsx";
import { FaPlus, FaExternalLinkAlt, FaShare, FaSync } from 'react-icons/fa';
import { BsThreeDots } from 'react-icons/bs';
import { Chrome, Compass } from 'lucide-react';

import PaymentScreen from "./PaymentScreen";
import SettingsPage from "./SettingsPage";
import { LinkService } from "../Services/LinkService.jsx";
import { AnalyticService } from "../Services/AnalyticService.jsx";
import { ENDPOINTS } from "../Utils/APIConstants.jsx";
import PostPreviewModal from "./PostPreviewModal"; // adjust path as needed
import EditPreviewModal from "../components/Modals/EditPreviewModal";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { DebugProvider } from "../contexts/DebugContext";
import { DebugStateViewer } from "../components/Debug/DebugMenu";
import noDataFoundImage from '../assets/no_data_found.jpg';
import DebugToggle from "../components/Debug/DebugToggle";
import { UserStatusProvider, useUserStatus } from "../contexts/UserStatusContext";
import RemainingLinksDebugPanel from "../components/Debug/RemainingLinksDebugPanel";
import RemainingLinksCard from "../components/UI/RemainingLinksCard";
import UpgradeModal from "../components/UI/UpgradeModal";
import UpgradeCelebrationModal from "../components/UI/UpgradeCelebrationModal";
import DashboardLoading from "../components/Loading/DashboardLoading";
import EnvironmentUtils from "../Utils/EnvironmentUtils.js";
import { WEBSITE_NAME, SHARING_TITLE, SHARING_DESCRIPTION, WEBSITE_URL } from "../config/branding.js";
import referIllLeft from '../assets/refer_ill_left.png';
import referIllRight from '../assets/refer_ill_right.png';


// Cache for analytics data to prevent regeneration on re-renders
// Enhanced: store a signature of the analytics object to avoid serving stale cached data
// Map<linkId, { data: AnalyticsDisplayItem[], signature: string }>
const analyticsCache = new Map();

// Function to get analytics data for selected link using real API data
const getAnalyticsDataForLink = (selectedLink) => {
  if (!selectedLink) {
    return [
      { label: "Page Visits", value: "--", progress: 0 },
      { label: "Link Redirects", value: "--", progress: 0 },
      { label: "Unique Visitors", value: "--", progress: 0 },
      { label: "Returning Visitors", value: "--", progress: 0 },
      { label: "Avg. Session Duration", value: "--", progress: 0 },
      { label: "Top Country", value: "--", progress: 0 },
    ];
  }
  
  // If we have cached data, ensure it matches the current analytics payload
  if (analyticsCache.has(selectedLink.id)) {
    const cached = analyticsCache.get(selectedLink.id);
    const currentSignature = JSON.stringify(selectedLink.analytics || {});
    if (cached && cached.signature === currentSignature) {
      return cached.data;
    }
    // Signature changed -> fall through and regenerate
  }
  
  // Use real analytics data from the API response
  // selectedLink now contains analytics data fetched from the server
  const analytics = selectedLink.analytics || {};

  // Helper: parse common duration strings to seconds (e.g., "1m 30s", "90s", "01:30")
  const parseDurationToSeconds = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number' && !isNaN(value)) return Math.max(0, Math.round(value));
    if (typeof value !== 'string') return 0;
    const v = value.trim().toLowerCase();
    // mm:ss
    const mmss = v.match(/^(\d{1,3}):(\d{1,2})$/);
    if (mmss) {
      const m = parseInt(mmss[1], 10) || 0;
      const s = parseInt(mmss[2], 10) || 0;
      return m * 60 + s;
    }
    // Xm Ys, X min Y sec, etc.
    const m = v.match(/(\d+)\s*m(?:in(?:ute)?s?)?/);
    const s = v.match(/(\d+)\s*s(?:ec(?:ond)?s?)?/);
    if (m || s) {
      const minutes = m ? parseInt(m[1], 10) || 0 : 0;
      const seconds = s ? parseInt(s[1], 10) || 0 : 0;
      return minutes * 60 + seconds;
    }
    // Xs
    const onlyS = v.match(/^(\d+)\s*s(?:ec(?:ond)?s?)?$/);
    if (onlyS) return parseInt(onlyS[1], 10) || 0;
    // Xm
    const onlyM = v.match(/^(\d+)\s*m(?:in(?:ute)?s?)?$/);
    if (onlyM) return (parseInt(onlyM[1], 10) || 0) * 60;
    // Fallback: numeric string in seconds
    const asNum = parseInt(v, 10);
    return isNaN(asNum) ? 0 : Math.max(0, asNum);
  };

  // Helper: attempt to derive avg session duration (in seconds) from multiple possible fields
  const getAvgSessionDurationSeconds = (an) => {
    if (!an || typeof an !== 'object') return 0;
    // Prefer explicit seconds fields
    const secondsCandidates = [
      'avgSessionDurationSeconds',
      'averageSessionDurationSeconds',
      'avg_session_duration_seconds',
      'avgDurationSeconds',
      'avg_session_duration_secs'
    ];
    for (const k of secondsCandidates) {
      if (an[k] != null && !isNaN(parseFloat(an[k]))) return Math.max(0, Math.round(parseFloat(an[k])));
    }
    // Sometimes provided as minutes
    const minuteCandidates = [
      'avgSessionDurationMinutes',
      'averageSessionDurationMinutes',
      'avg_session_duration_minutes'
    ];
    for (const k of minuteCandidates) {
      if (an[k] != null && !isNaN(parseFloat(an[k]))) return Math.max(0, Math.round(parseFloat(an[k]) * 60));
    }
    // Formatted string candidates
    const stringCandidates = [
      'avgSessionDuration',
      'averageSessionDuration',
      'avg_session_duration',
      'averageSessionDurationFormatted'
    ];
    for (const k of stringCandidates) {
      if (an[k] != null) return parseDurationToSeconds(an[k]);
    }
    // Derive from totals if present
    const totals = [
      ['totalSessionDurationSeconds', 'sessionCount'],
      ['total_session_duration_seconds', 'session_count'],
      ['totalDurationSeconds', 'totalSessions']
    ];
    for (const [totalKey, countKey] of totals) {
      const total = parseFloat(an[totalKey]);
      const count = parseFloat(an[countKey]);
      if (!isNaN(total) && !isNaN(count) && count > 0) return Math.max(0, Math.round(total / count));
    }
    return 0;
  };

  // LIVE DATA: All links are summaries, so we get comprehensive analytics
  console.log('\n🎯 === ANALYTICS DISPLAY DEBUG ===');
  console.log('🔍 Processing analytics for:', selectedLink.slug || selectedLink.id);
  console.log('📊 Raw analytics object:', analytics);
  
  // Extract metrics from the summary analytics endpoint
  const pageVisits = analytics.pageVisits || 0; // summary_page_view clicks
  const redirectClicks = analytics.redirectClicks || 0; // summary_redirect clicks (usually 0)
  const originalLinkClicks = analytics.originalLinkClicks || 0; // original link clicks (meaningful data)
  
  // Combine redirect clicks and original link clicks into "Link Redirects"
  // Use originalLinkClicks as the primary value since redirectClicks is always 0
  const linkRedirects = originalLinkClicks; // This is where the real engagement data is
  
  const uniqueVisitors = analytics.uniqueVisitors || 0;
  const returningVisitors = analytics.returningVisitors || 0;
  // Normalize average session duration to seconds (robust across API variants)
  const avgSessionDuration = getAvgSessionDurationSeconds(analytics);
  const topCountry = analytics.topCountry || "N/A";
  
  console.log('📋 === EXTRACTED VALUES FOR DISPLAY ===');
  console.log('👁️ Page Visits (for display):', pageVisits);
  console.log('🔗 Redirect Clicks (for display):', redirectClicks);
  console.log('📎 Original Link Clicks (for display):', originalLinkClicks);
  console.log('👥 Unique Visitors (for display):', uniqueVisitors);
  console.log('🔄 Returning Visitors (for display):', returningVisitors);
  console.log('⏱️ Session Duration (for display):', avgSessionDuration);
  console.log('🏳️ Top Country (for display):', topCountry);
  
  // Format session duration from seconds to readable format
  const formatSessionDuration = (value) => {
    // If the value is already a formatted string (like "18m 35s"), return it as-is
    if (typeof value === 'string' && value.match(/^\d+[ms]( \d+s)?$/)) {
      return value;
    }
    
    // Handle NaN, null, undefined, or invalid numeric values
    if (!value || value === 0 || isNaN(value) || typeof value !== 'number') {
      return "<1 min";
    }
    
    // Ensure it's a positive number
    const validSeconds = Math.max(0, Math.round(value));
    
    const minutes = Math.floor(validSeconds / 60);
    const remainingSeconds = validSeconds % 60;
    
    // Double check for NaN in calculations
    if (isNaN(minutes) || isNaN(remainingSeconds)) {
      return "<1 min";
    }
    
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };
  
  // Convert country code to readable format
  const formatCountryName = (countryCode) => {
    const countryMap = {
      "US": "United States",
      "CA": "Canada", 
      "GB": "United Kingdom",
      "UK": "United Kingdom",
      "DE": "Germany",
      "AU": "Australia",
      "FR": "France",
      "ES": "Spain",
      "IT": "Italy",
      "JP": "Japan",
      "CN": "China",
      "IN": "India",
      "BR": "Brazil"
    };
    return countryMap[countryCode] || countryCode || "N/A";
  };
  
  const analyticsData = [
    { 
      label: "Page Visits", 
      value: pageVisits.toLocaleString(), 
      progress: Math.min((pageVisits / 1000) * 100, 100) // Progress based on 1000 visit goal
    },
    { 
      label: "Link Redirects", 
      value: linkRedirects.toLocaleString(), 
      progress: Math.min((linkRedirects / 500) * 100, 100) // Progress based on 500 redirect goal
    },
    { 
      label: "Unique Visitors", 
      value: uniqueVisitors.toLocaleString(), 
      progress: Math.min((uniqueVisitors / 750) * 100, 100) // Progress based on 750 unique visitor goal
    },
    { 
      label: "Returning Visitors", 
      value: returningVisitors.toLocaleString(), 
      progress: Math.min((returningVisitors / 250) * 100, 100) // Progress based on 250 return visitor goal
    },
    { 
      label: "Avg. Session Duration", 
      value: formatSessionDuration(avgSessionDuration), 
      progress: (() => {
        // Convert string duration back to seconds for progress calculation
        if (typeof avgSessionDuration === 'string') {
          const totalSeconds = parseDurationToSeconds(avgSessionDuration);
          return Math.min((totalSeconds / 600) * 100, 100);
        }
        // For numeric values (seconds)
        const numericDuration = typeof avgSessionDuration === 'number' ? avgSessionDuration : 0;
        return Math.min((numericDuration / 600) * 100, 100); // Progress based on 10 minute (600s) goal
      })()
    },
    { 
      label: "Top Country", 
      value: formatCountryName(topCountry),
      progress: pageVisits > 0 ? Math.min((pageVisits / 100) * 100, 100) : 0 // Progress based on total visits
    },
  ];
  
  console.log('📋 === FINAL ANALYTICS DATA FOR UI ===');
  analyticsData.forEach((item, index) => {
    console.log(`${index + 1}. ${item.label}: ${item.value} (${item.progress.toFixed(1)}% progress)`);
  });
  console.log('=== END ANALYTICS DISPLAY DEBUG ===\n');
  
  // Cache the generated data with a signature of the current analytics
  const signature = JSON.stringify(selectedLink.analytics || {});
  analyticsCache.set(selectedLink.id, { data: analyticsData, signature });
  
  return analyticsData;
};



// Helper function to get the color for a specific link
const getLinkColor = (linkId, allLinks) => {
  if (!linkId || !allLinks) return '#31BA96'; // Default green fallback
  
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
  const linkIndex = allLinks.findIndex(link => link.id === linkId);
  
  if (linkIndex === -1) return '#31BA96'; // Default green fallback
  
  return colors[linkIndex % colors.length];
};

const DashScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    getPlanDisplayName, 
    canCreateLinks, 
    showUpgradeCelebration, 
    closeUpgradeCelebration,
    userStatus,
    isPro,
    isStarter,
    refreshUserStatus
  } = useUserStatus();
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalTrigger, setUpgradeModalTrigger] = useState('create');
  const [activeMenu, setActiveMenu] = useState("Dashboard");
  const [loading, setLoading] = useState(true);
  const [linkListings, setLinkListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Search functionality state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredListings, setFilteredListings] = useState([]);
  
  // Selection mode state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  
  // Selected link for analytics/impressions state
  const [selectedLinkForAnalytics, setSelectedLinkForAnalytics] = useState(null);

  const statusPollingRef = useRef(null);
  const processingIdsRef = useRef([]);
  
  // Selected day for impressions chart state
  const [selectedDay, _setSelectedDay] = useState(null);
  
  const setSelectedDay = React.useCallback((value) => {
    _setSelectedDay(value);
  }, []);
  
  // Clipboard banner state
  const [showClipboardBanner, setShowClipboardBanner] = useState(false);
  const [clipboardMessage, setClipboardMessage] = useState('');
  
  // Refresh analytics state
  const [isRefreshingAnalytics, setIsRefreshingAnalytics] = useState(false);
  
  // Impressions timeline state
  const [impressionsData, setImpressionsData] = useState(null);
  const [isLoadingImpressions, setIsLoadingImpressions] = useState(false);
  const [impressionsError, setImpressionsError] = useState(null);
  const [showMCPBanner, setShowMCPBanner] = useState(() => {
    // Check if user has dismissed the banner
    return !localStorage.getItem('mcp-banner-dismissed');
  });
  
  // Fetch impressions timeline data
  const fetchImpressionsTimeline = async (link) => {
    if (!link) {
      setImpressionsData(null);
      return;
    }
    
    console.log('📊 Fetching impressions timeline for:', link.slug || link.id);
    setIsLoadingImpressions(true);
    setImpressionsError(null);
    
    try {
      let timelineData;
      
      // Try summary timeline first (preferred for most links)
      if (link.summaryId) {
        console.log('📄 Fetching summary impressions timeline for summaryId:', link.summaryId);
        timelineData = await AnalyticService.fetchSummaryImpressionsTimeline(link.summaryId, 30);
      } else {
        console.log('🔗 Fetching link impressions timeline for linkId:', link.id);
        timelineData = await AnalyticService.fetchLinkImpressionsTimeline(link.id, 30);
      }
      
      console.log('✅ Impressions timeline data received:', timelineData.data);
      
      // DEBUG: Log the raw API response
      console.log('🔍 DashScreen DEBUG - Raw API response:', timelineData);
      if (timelineData.data?.dailyData) {
        console.log('🔍 DashScreen DEBUG - API daily data:');
        timelineData.data.dailyData.forEach((day, index) => {
          console.log(`  API Day ${index}:`, {
            date: day.date,
            day: day.day,
            count: day.count,
            isToday: day.isToday
          });
        });
        console.log('🔍 DashScreen DEBUG - Total impressions:', timelineData.data.totalImpressions);
        console.log('🔍 DashScreen DEBUG - Date range:', timelineData.data.dateRange);
        console.log('🔍 DashScreen DEBUG - Has any data:', timelineData.data.dailyData.some(day => day.count > 0));
      }
      
      setImpressionsData(timelineData.data);
      
    } catch (error) {
      console.error('❌ Error fetching impressions timeline:', error);
      setImpressionsError(error.message || 'Failed to load impressions data');
      
      // Set fallback empty data to prevent crashes
      setImpressionsData({
        totalImpressions: 0,
        dailyData: Array.from({ length: 7 }, (_, i) => {
          const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
          return {
            date: new Date().toISOString().split('T')[0],
            day: dayNames[i],
            count: 0,
            isToday: i === new Date().getDay() - 1 || (new Date().getDay() === 0 && i === 6)
          };
        })
      });
    } finally {
      setIsLoadingImpressions(false);
    }
  };
  
  // Handle analytics refresh
  const handleRefreshAnalytics = async () => {
    if (!selectedLinkForAnalytics || isRefreshingAnalytics) return;
    
    console.log('🔄 Refreshing analytics for:', selectedLinkForAnalytics.slug || selectedLinkForAnalytics.id);
    setIsRefreshingAnalytics(true);
    
    try {
      let analyticsData;
      
      try {
        // Try to use summary analytics first (all links lead to summaries)
        console.log('📊 Fetching fresh summary analytics for:', selectedLinkForAnalytics.slug || selectedLinkForAnalytics.id);
        analyticsData = await AnalyticService.fetchSummaryAnalytics(selectedLinkForAnalytics.summaryId || selectedLinkForAnalytics.id);
        console.log('✅ Fresh summary analytics success for:', selectedLinkForAnalytics.slug);
      } catch (error) {
        console.warn('⚠️ Summary analytics failed for:', selectedLinkForAnalytics.slug, 'Error:', error.message);
        console.log('🔄 Falling back to link analytics for:', selectedLinkForAnalytics.slug);
        
        try {
          // Fallback to regular link analytics
          analyticsData = await AnalyticService.fetchLinkAnalytics(selectedLinkForAnalytics.id);
          console.log('✅ Link analytics fallback success for:', selectedLinkForAnalytics.slug);
        } catch (fallbackError) {
          console.error('❌ Both analytics methods failed for:', selectedLinkForAnalytics.slug, fallbackError);
          throw fallbackError; // Re-throw to be caught by outer try-catch
        }
      }
      
      // Update the linkListings with fresh analytics data
      setLinkListings(prevListings => 
        prevListings.map(listing => 
          listing.id === selectedLinkForAnalytics.id 
            ? {
                ...listing,
                totalClicks: analyticsData.data.totalClicks,
                analytics: analyticsData.data
              }
            : listing
        )
      );
      
      // Clear analytics cache for this link to force re-calculation
      analyticsCache.delete(selectedLinkForAnalytics.id);
      
      // Update the selected link for analytics with fresh data
      const updatedLink = {
        ...selectedLinkForAnalytics,
        totalClicks: analyticsData.data.totalClicks,
        analytics: analyticsData.data
      };
      
      setSelectedLinkForAnalytics(updatedLink);
      
      // Also refresh impressions timeline data
      await fetchImpressionsTimeline(updatedLink);
      
      console.log('✅ Analytics and impressions refreshed successfully for:', selectedLinkForAnalytics.slug);
      
      // Show success feedback
      setClipboardMessage('✅ Analytics refreshed successfully!');
      setShowClipboardBanner(true);
      setTimeout(() => setShowClipboardBanner(false), 3000);
      
    } catch (error) {
      console.error('❌ Error refreshing analytics:', error);
      
      // Show error feedback
      setClipboardMessage('❌ Failed to refresh analytics');
      setShowClipboardBanner(true);
      setTimeout(() => setShowClipboardBanner(false), 3000);
    } finally {
      setIsRefreshingAnalytics(false);
    }
  };
  
  // Handle share button click
  const handleShareLink = async (listing, e) => {
    e.stopPropagation();
    
    try {
      // Use the main domain with /summary/ route for iMessage previews
      let shareUrl = null;
      if (listing?.slug) {
        shareUrl = `https://threadshare.ai/summary/${listing.slug}`;
      } else if (listing?.shortUrl) {
        // Fallback: preserve prior behavior if slug is missing
        shareUrl = EnvironmentUtils.updateDomainToShareThreadShare(listing.shortUrl);
      }

      if (!shareUrl) {
        setClipboardMessage('❌ No shareable URL available for this link');
        setShowClipboardBanner(true);
        setTimeout(() => setShowClipboardBanner(false), 3000);
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      setClipboardMessage('✅ Share URL copied to clipboard!');
      setShowClipboardBanner(true);
      setTimeout(() => setShowClipboardBanner(false), 3000);

      console.log('✅ Share URL copied:', shareUrl);
    } catch (error) {
      console.error('❌ Failed to copy to clipboard:', error);
      setClipboardMessage('❌ Failed to copy link');
      setShowClipboardBanner(true);
      setTimeout(() => setShowClipboardBanner(false), 3000);
    }
  };
  
  
  // Helper functions for localStorage persistence
  const saveSelectedLinkToStorage = (linkId) => {
    if (linkId) {
      localStorage.setItem('selectedLinkForAnalytics', linkId);
    } else {
      localStorage.removeItem('selectedLinkForAnalytics');
    }
  };
  
  const getSelectedLinkFromStorage = () => {
    return localStorage.getItem('selectedLinkForAnalytics');
  };
  
  // Handle link selection for analytics
  const handleLinkSelectionForAnalytics = async (link) => {
    console.log('\n🔍 === LINK SELECTION DEBUG ===');
    console.log('📝 Selected link object:', link);
    console.log('📊 Analytics data attached to link:', link?.analytics);
    console.log('🆔 Link ID:', link?.id);
    console.log('🏷️ Summary ID:', link?.summaryId);
    console.log('📈 Total Clicks from server:', link?.totalClicks);
    
    if (link?.analytics) {
      console.log('\n📊 === SERVER ANALYTICS VALUES ===');
      console.log('👁️ Page Visits:', link.analytics.pageVisits);
      console.log('🔗 Redirect Clicks:', link.analytics.redirectClicks);
      console.log('📎 Original Link Clicks:', link.analytics.originalLinkClicks);
      console.log('👥 Total Clicks:', link.analytics.totalClicks);
      console.log('🌍 Unique Visitors:', link.analytics.uniqueVisitors);
      console.log('🔄 Returning Visitors:', link.analytics.returningVisitors);
      console.log('⏱️ Avg Session Duration:', link.analytics.avgSessionDuration);
      console.log('🏳️ Top Country:', link.analytics.topCountry);
      console.log('🗺️ Country Breakdown:', link.analytics.countryBreakdown);
      console.log('📍 Top Referrers:', link.analytics.topReferrers);
      console.log('📊 Click Types Breakdown:', link.analytics.clicksByType);
    } else {
      console.log('❌ No analytics data found on selected link');
    }
    
    setSelectedLinkForAnalytics(link);
    saveSelectedLinkToStorage(link?.id);
    setSelectedDay(null); // Reset selected day when changing links
    
    // Fetch impressions timeline data for the selected link
    await fetchImpressionsTimeline(link);
    
    console.log('✅ Link selected for analytics:', link?.linkTitle || link?.slug);
    console.log('=== END SELECTION DEBUG ===\n');
  };
  
  // Handle day selection from bar chart
  const handleDaySelection = (dayData) => {
    setSelectedDay(dayData);
  };
  
  // Get the display count for the circle (selected day or total)
  const getDisplayCount = () => {
    if (selectedDay) {
      return selectedDay.impressions;
    }
    // Use impressions data if available, otherwise fall back to totalClicks
    if (impressionsData?.totalImpressions !== undefined) {
      return impressionsData.totalImpressions;
    }
    return selectedLinkForAnalytics?.totalClicks || 0;
  };



  
    // Use centralized profile context
    const { getUserData } = useUserProfile();
    const profileUserData = getUserData();
    
    const userName = profileUserData.name;
    const userImage = profileUserData.image;
    const userEmail = profileUserData.email;

  // Handle Stripe Checkout return (success/cancel)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    const sessionId = urlParams.get('session_id');

    if (checkoutStatus === 'success') {
      console.log('🎉 Checkout successful! Session ID:', sessionId);
      
      // FAILSAFE: Force sync with Stripe to ensure status is updated
      // This bypasses webhook failures by directly querying Stripe
      const syncWithStripe = async () => {
        try {
          console.log('🔄 Forcing subscription sync with Stripe...');
          const { SubscriptionService } = await import('../Services/SubscriptionService');
          await SubscriptionService.syncFromStripe();
          console.log('✅ Subscription synced with Stripe successfully');
          
          // Then refresh user status from backend
          await refreshUserStatus();
          console.log('✅ User status refreshed after checkout success');
        } catch (error) {
          console.error('❌ Failed to sync subscription:', error);
          // Fallback to regular refresh
          try {
            await refreshUserStatus();
          } catch (refreshError) {
            console.error('❌ Failed to refresh user status:', refreshError);
          }
        }
      };
      
      syncWithStripe();

      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutStatus === 'cancel') {
      console.log('❌ Checkout cancelled');
      
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // Run once on mount

  // Listen for UserStatusContext changes to force re-render
  useEffect(() => {
    console.log('🗺 DashScreen: User status changed:', {
      userStatus,
      planDisplayName: getPlanDisplayName(),
      isPro: isPro(),
      isStarter: isStarter()
    });
    // Profile data is now managed by UserProfileContext, no need to fetch here
  }, [userStatus, getPlanDisplayName, isPro, isStarter]);


  // Fetch users from API on component mount
  useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await LinkService.fetchAllLinks();

      const formattedData = await Promise.all(
        response.data.map(async (item) => {
          let analyticsData;
          const isProcessing = item.status === 'processing';
          const isFailed = item.status === 'failed';
          
          if (isProcessing || isFailed) {
            analyticsData = {
              data: {
                totalClicks: 0,
                pageVisits: 0,
                redirectClicks: 0,
                originalLinkClicks: 0,
                uniqueVisitors: 0,
                returningVisitors: 0,
                avgSessionDuration: 0,
                topCountry: 'N/A'
              }
            };
          } else {
            try {
              // Try to use summary analytics first (all links lead to summaries)
              console.log('\n🔍 === ANALYTICS FETCH DEBUG FOR LINK ===');
              console.log('📝 Link Details:');
              console.log('  - ID:', item.id);
              console.log('  - Slug:', item.slug);
              console.log('  - Title:', item.linkTitle);
              console.log('  - Summary ID:', item.summaryId);
              console.log('  - User ID:', item.userId);
              console.log('  - Created:', item.createdAt);
              // Check if we have a valid summaryId before trying summary analytics
              if (!item.summaryId || item.summaryId === 'null' || item.summaryId === null) {
                console.log('⚠️ No valid summaryId found, trying to find summary by slug');
                
                // Try to use the same slug to find the summary
                if (item.slug) {
                  console.log('🔍 Attempting to find summary using slug:', item.slug);
                  try {
                    analyticsData = await AnalyticService.fetchSummaryAnalytics(item.slug);
                    console.log('✅ Successfully found summary by slug!', item.slug);
                  } catch (slugError) {
                    console.log('❌ Summary by slug failed, falling back to link analytics');
                    throw new Error('No valid summaryId and slug lookup failed - using link analytics instead');
                  }
                } else {
                  console.log('❌ No slug available either, using link analytics fallback');
                  throw new Error('No valid summaryId - using link analytics instead');
                }
              } else {
                console.log('📊 Fetching summary analytics for:', item.slug || item.id, 'summaryId:', item.summaryId);
                analyticsData = await AnalyticService.fetchSummaryAnalytics(item.summaryId);
              }
              console.log('✅ Summary analytics success for:', item.slug);
              console.log('📊 Raw analytics response:', analyticsData);
            } catch (error) {
              console.warn('⚠️ Summary analytics failed for:', item.slug, 'Error:', error.message);
              console.log('🔄 Falling back to link analytics for:', item.slug);
              
              try {
                // Fallback to regular link analytics
                console.log('🔄 Trying fallback link analytics for:', item.slug);
                analyticsData = await AnalyticService.fetchLinkAnalytics(item.id);
                console.log('✅ Link analytics fallback success for:', item.slug);
                console.log('📊 Fallback analytics response:', analyticsData);
              } catch (fallbackError) {
                console.error('❌ Both analytics methods failed for:', item.slug, fallbackError);
                console.log('⚠️ Creating empty analytics fallback for:', item.slug);
                // Create empty analytics data to prevent crashes
                analyticsData = {
                  data: {
                    totalClicks: 0,
                    pageVisits: 0,
                    redirectClicks: 0,
                    originalLinkClicks: 0,
                    uniqueVisitors: 0,
                    returningVisitors: 0,
                    avgSessionDuration: 0,
                    topCountry: 'N/A'
                  }
                };
                console.log('📊 Empty fallback analytics:', analyticsData);
              }
            }
          }
          
          const finalLinkData = {
            ...item,
            formattedDate: new Date(item.createdAt).toLocaleDateString(),
            totalClicks: analyticsData.data.totalClicks, // attach analytics to each item
            analytics: analyticsData.data, // Store full analytics for use in getAnalyticsDataForLink
          };
          
          console.log('🔗 Final link data constructed:');
          console.log('  - totalClicks:', finalLinkData.totalClicks);
          console.log('  - analytics.pageVisits:', finalLinkData.analytics.pageVisits);
          console.log('  - analytics.originalLinkClicks:', finalLinkData.analytics.originalLinkClicks);
          console.log('  - analytics.avgSessionDuration:', finalLinkData.analytics.avgSessionDuration);
          console.log('=== END ANALYTICS FETCH DEBUG ===\n');
          
          return finalLinkData;
        })
      );

      // Sort by recency (most recent first) using createdAt; fall back to updatedAt if missing
      const sortedByRecency = [...formattedData].sort((a, b) => {
        const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return tb - ta; // descending
      });

      setLinkListings(sortedByRecency);
      
      // Debug: Compare links with data vs without data
      console.log('\n📊 === ANALYTICS COMPARISON SUMMARY ===');
      const linksWithData = formattedData.filter(link => link.totalClicks > 0);
      const linksWithoutData = formattedData.filter(link => link.totalClicks === 0);
      
      console.log(`✅ Links WITH data (${linksWithData.length}):`);
      linksWithData.forEach(link => {
        console.log(`  - ${link.slug}: ${link.totalClicks} clicks, summaryId: ${link.summaryId}`);
      });
      
      console.log(`❌ Links WITHOUT data (${linksWithoutData.length}):`);
      linksWithoutData.forEach(link => {
        console.log(`  - ${link.slug}: ${link.totalClicks} clicks, summaryId: ${link.summaryId}`);
      });
      
      console.log('=== END COMPARISON SUMMARY ===\n');

    } catch (error) {
      console.error('Error fetching links:', error);
      
      // Handle token expiration cases
      if (error.response?.status === 401 || error.response?.status === 403) {
        // LinkService already handles the redirect, so we just return here
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  const normalizeStatusItems = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.links)) return payload.links;
    if (Array.isArray(payload?.statuses)) return payload.statuses;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  useEffect(() => {
    const processingIds = linkListings
      .filter((link) => link.status === 'processing')
      .map((link) => link.id)
      .filter(Boolean);

    processingIdsRef.current = processingIds;

    if (processingIds.length === 0) {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
      return;
    }

    const pollStatuses = async () => {
      const ids = processingIdsRef.current;
      if (!ids.length) return;
      try {
        const response = await LinkService.fetchLinkStatuses(ids);
        const statusItems = normalizeStatusItems(response?.data);
        if (!statusItems.length) return;

        const statusMap = new Map();
        statusItems.forEach((item) => {
          const id = item?.id || item?.linkId;
          if (id) {
            statusMap.set(id, item);
          }
        });

        if (!statusMap.size) return;

        setLinkListings((prev) =>
          prev.map((link) => {
            const update = statusMap.get(link.id);
            if (!update) return link;
            return {
              ...link,
              ...update,
              formattedDate: link.formattedDate || new Date(link.createdAt).toLocaleDateString()
            };
          })
        );
      } catch (error) {
        console.error('❌ Error polling link statuses:', error);
      }
    };

    pollStatuses();
    statusPollingRef.current = setInterval(pollStatuses, 4000);

    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
    };
  }, [linkListings]);

  // Search filtering logic
  const filterListings = (listings, term) => {
    if (!term.trim()) {
      return listings;
    }
    
    const searchTermLower = term.toLowerCase().trim();
    return listings.filter((listing) => {
      return (
        listing.slug?.toLowerCase().includes(searchTermLower) ||
        listing.linkTitle?.toLowerCase().includes(searchTermLower) ||
        listing.shortSummaryDescription?.toLowerCase().includes(searchTermLower) ||
        listing.caption?.toLowerCase().includes(searchTermLower) ||
        listing.userId?.toLowerCase().includes(searchTermLower) ||
        listing.originalUrl?.toLowerCase().includes(searchTermLower) ||
        listing.shortUrl?.toLowerCase().includes(searchTermLower)
      );
    });
  };

  // Update filtered listings when linkListings or searchTerm changes
  useEffect(() => {
    setFilteredListings(filterListings(linkListings, searchTerm));
  }, [linkListings, searchTerm]);
  

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchTerm('');
  };

  // Selection mode handlers
  const handleThreeDotsClick = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems([]); // Clear selections when toggling mode
  };

  const handleItemSelection = (itemId) => {
    setSelectedItems(prevSelected => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter(id => id !== itemId);
      } else {
        return [...prevSelected, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredListings.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredListings.map(listing => listing.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    // Confirm deletion
    const confirmMessage = `Are you sure you want to delete ${selectedItems.length} selected link${selectedItems.length > 1 ? 's' : ''}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeletingBulk(true);
    const itemsToDelete = [...selectedItems]; // Create a copy
    let successCount = 0;
    let failedItems = [];

    try {
      // Debug: Check user token and link data
      const userData = StorageUtils.getUserData();
      console.log('Debug - User token exists:', !!userData?.token);
      console.log('Debug - Links to delete:', itemsToDelete.map(id => ({
        id,
        linkData: linkListings.find(l => l.id === id)
      })));
      
      console.log(`Starting bulk delete of ${itemsToDelete.length} items:`, itemsToDelete);
      
      // Delete all selected items with individual error handling
      const deletePromises = itemsToDelete.map(async (itemId) => {
        try {
          const linkData = linkListings.find(l => l.id === itemId);
          console.log(`🔍 Attempting to delete item ${itemId}:`);
          console.log(`   - Link title: ${linkData?.linkTitle || 'N/A'}`);
          console.log(`   - Link ID type: ${typeof itemId}`);
          console.log(`   - Link ID value: '${itemId}'`);
          console.log(`   - Full DELETE URL will be: ${ENDPOINTS.CREATE_LINK}/${itemId}`);
          
          const response = await LinkService.deleteLink(itemId);
          successCount++;
          console.log(`✅ Successfully deleted item ${itemId}`);
          return { itemId, success: true };
        } catch (error) {
          console.error(`❌ Failed to delete item ${itemId}:`);
          console.error('Error details:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers,
            config: {
              url: error.config?.url,
              method: error.config?.method,
              headers: error.config?.headers
            },
            stack: error.stack
          });
          failedItems.push(itemId);
          return { itemId, success: false, error };
        }
      });

      await Promise.all(deletePromises);
      
      // Update the listings state - remove successfully deleted items
      const successfullyDeleted = itemsToDelete.filter(id => !failedItems.includes(id));
      setLinkListings(prevListings => 
        prevListings.filter(listing => !successfullyDeleted.includes(listing.id))
      );
      
      // Exit selection mode and clear selections
      setIsSelectionMode(false);
      setSelectedItems([]);
      
      // Show results to user
      if (failedItems.length === 0) {
        console.log(`✅ Successfully deleted all ${successCount} selected items`);
        // You could show a success toast here
      } else {
        console.log(`⚠️ Deleted ${successCount} items successfully, ${failedItems.length} failed`);
        alert(`Deleted ${successCount} items successfully. ${failedItems.length} items failed to delete.`);
      }
      
    } catch (error) {
      console.error('Error during bulk delete operation:', error);
      alert('An error occurred during the bulk delete operation. Some items may not have been deleted.');
    } finally {
      setIsDeletingBulk(false);
    }
  };

  const handleEdit = (itemId) => {
    // Find the selected item
    const itemToEdit = linkListings.find(listing => listing.id === itemId);
    
    if (itemToEdit) {
      console.log('Editing item:', itemToEdit);
      setEditingItem(itemToEdit);
      setShowEditModal(true);
      
      // Exit selection mode after opening edit modal
      setIsSelectionMode(false);
      setSelectedItems([]);
    } else {
      console.error('Item not found for editing:', itemId);
    }
  };

  const handleSaveEdit = async (updatedData) => {
    try {
      // Here you would typically call an API to update the link
      // For now, we'll just update the local state
      console.log('Saving updated data:', updatedData);
      
      // Update the linkListings state with the new data
      setLinkListings(prevListings => 
        prevListings.map(listing => 
          listing.id === updatedData.id ? updatedData : listing
        )
      );
      
      console.log('Link updated successfully');
    } catch (error) {
      console.error('Error updating link:', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  // Handle navigation state from TopNavbar mobile links
  useEffect(() => {
    if (location.state?.activeMenu) {
      setActiveMenu(location.state.activeMenu);
      // Clear the state to prevent it from persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    console.log(`Navigated to: ${menu}`);
  };

  if (loading) {
    return <DashboardLoading />;
  }

   const handleClick = async(listing) => {
    // Select the listing for analytics display (keeps user in dashboard)
    setSelectedListing(listing);
    console.log('Selected listing for analytics:', listing.slug);
    
    // Fetch fresh analytics data for the selected link
    try {
      const analyticsResponse = await AnalyticService.fetchLinkAnalytics(listing.id);
      console.log('Analytics data for selected link:', analyticsResponse.data);
      
      // The analytics panel will now show data for this selected link
      // No navigation away from dashboard - user stays in context
      
    } catch (error) {
      console.log('Error fetching analytics for selected link:', error);
    }
  };

  

  const handleRedirect = () => {
    // Check if user can create links
    if (!canCreateLinks()) {
      setUpgradeModalTrigger('plus');
      setShowUpgradeModal(true);
      return;
    }
    
    navigate("/create");
  };

  return (
    <Container fluid className="dashboard-container" style={{ overflow: "auto", minHeight: "100vh" }}>
      {/* Debug Toggle Button */}
      <DebugToggle position="bottom-left" />
      
      {/* Remaining Links Debug Panel */}
      <RemainingLinksDebugPanel />
      
      {/* Debug State Viewer for Dashboard State */}
      <DebugStateViewer 
        state={{
          activeMenu,
          linkListings: linkListings.length > 0 ? linkListings.slice(0, 2) : [], // Show first 2 items only for debug
          userName,
          userEmail,
          loading,
          // User status debug info
          userStatus,
          planDisplayName: getPlanDisplayName(),
          isPro: isPro(),
          isStarter: isStarter()
        }}
        title="Dashboard State"
        position="bottom-right"
        maxHeight="250px"
        maxWidth="350px"
      />
      <Row>
        {/* Sidebar */}
          <div className="col-md-3 d-none d-md-block" style={{ marginLeft: "0px", paddingLeft: "0px", marginBottom: "0px", paddingBottom: "0px" }}>

          <Col className="sidebar">
          <div className="profile-section text-center d-flex flex-column align-items-center mb-4">
            <img
              src={userImage || "/assets/default_user.png"} // Fallback image
              alt="Profile"
              className="profile-picture rounded-circle mb-3"
              style={{ objectFit: 'cover', width: '80px', height: '80px' }}
            />
            <h5 className="mb-1">{userName}</h5>
            <p className="custom-email mb-0">{getPlanDisplayName()}</p>
          </div>
          <ListGroup className="menu">
            <ListGroup.Item
              className={`menu-item ${
                activeMenu === "Dashboard" ? "active" : ""
              }`}
              onClick={() => handleMenuClick("Dashboard")}
            >
              Dashboard
            </ListGroup.Item>
            <ListGroup.Item
              className={`menu-item ${
                activeMenu === "Payment" ? "active" : ""
              }`}
              onClick={() => handleMenuClick("Payment")}
            >
              Payment
            </ListGroup.Item>
            <ListGroup.Item
              className={`menu-item ${
                activeMenu === "Settings" ? "active" : ""
              }`}
              onClick={() => handleMenuClick("Settings")}
            >
              Settings
            </ListGroup.Item>
          </ListGroup>
        </Col>
        </div>
        

        {/* Main Content */}
        {activeMenu === "Dashboard" && <Col md={6} className="main-content">
          <Container className="mt-4" style={{ maxWidth: "900px" }}>
            
            {/* MCP Integration Promotional Banner */}
            {showMCPBanner && (
              <Row className="mt-4 mb-3">
                <Col>
                  <Card style={{ 
                    background: 'linear-gradient(180deg, #5AC8FA 0%, #007AFF 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 122, 255, 0.25)',
                    color: 'white'
                  }}>
                    <Card.Body style={{ padding: '1.5rem' }}>
                      <div className="d-flex justify-content-between align-items-start flex-wrap">
                        <div style={{ flex: 1, minWidth: '250px' }}>
                          <h5 style={{ color: 'white', fontWeight: '600', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                            <i className="fas fa-puzzle-piece" style={{ marginRight: '0.5rem' }}></i>
                            Get Started: Install Our Browser Extension
                          </h5>
                          <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                            Save conversations from ChatGPT, Gemini, or Claude with one click. Available for Chrome and Safari.
                          </p>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => navigate('/extension')}
                            style={{ 
                              fontWeight: '500',
                              fontSize: '0.875rem'
                            }}
                          >
                            Get Browser Extension
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
            
            <Row className="mt-4" style={{ marginBottom: "2rem" }}>
              <Col>
                <Card style={{ 
                  minHeight: "200px", 
                  border: '1px solid #f1f3f4',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Card.Body className="d-flex flex-column p-0" style={{ paddingBottom: "1rem" }}>
                    {/* Your Chats Header and Search Input */}
                    {linkListings.length === 0 ? <div></div> : <div style={{ padding: "1.25rem", paddingBottom: "0.75rem" }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.5rem', color: '#6b7280' }}>
                            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <h4 className="mb-0" style={{ fontSize: '1.375rem', fontWeight: '600', color: '#1a1a1a', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>Your Chats ({linkListings.length})</h4>
                        </div>
                        <Button
                          variant="link"
                          className="rounded-circle d-flex align-items-center justify-content-center p-0"
                          style={{
                            width: '32px',
                            height: '32px',
                            border: '1px solid #e5e7eb',
                            backgroundColor: '#ffffff',
                            color: '#6b7280',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f9fafb';
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.color = '#374151';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#ffffff';
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.color = '#6b7280';
                          }}
                          onClick={handleRedirect}
                          title="Create new link"
                        >
                          <FaPlus size={12} />
                        </Button>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1">
                          <SearchBar
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onClear={handleSearchClear}
                            placeholder="Search..."
                            className=""
                            showSearchIcon={true}
                            showClearButton={true}
                          />
                        </div>
                        <Button
                          variant="link"
                          className="d-flex align-items-center justify-content-center p-0"
                          style={{
                            width: '32px',
                            height: '32px',
                            backgroundColor: 'transparent',
                            color: '#6c757d',
                            border: 'none',
                            borderRadius: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8f9fa';
                            e.target.style.color = '#495057';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#6c757d';
                          }}
                          onClick={handleThreeDotsClick}
                          title={isSelectionMode ? "Exit selection mode" : "Select multiple items"}
                        >
                          <BsThreeDots size={16} />
                        </Button>
                      </div>
                      
                      {/* Selection Toolbar */}
                      {isSelectionMode && (
                        <div className="d-flex align-items-center justify-content-between mt-3 p-2 bg-light rounded" style={{ border: '1px solid #e9ecef' }}>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Check
                              type="checkbox"
                              checked={selectedItems.length === filteredListings.length && filteredListings.length > 0}
                              onChange={handleSelectAll}
                              label={`${selectedItems.length} of ${filteredListings.length} selected`}
                            />
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            {selectedItems.length > 0 && (
                              <>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleEdit(selectedItems[0])}
                                  disabled={selectedItems.length !== 1}
                                  style={{ 
                                    fontSize: '12px',
                                    opacity: selectedItems.length !== 1 ? 0.6 : 1
                                  }}
                                  title={selectedItems.length !== 1 ? "Select exactly one item to view" : "View selected item"}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={handleBulkDelete}
                                  disabled={isDeletingBulk}
                                  style={{ fontSize: '12px' }}
                                >
                                  {isDeletingBulk ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Deleting...
                                    </>
                                  ) : (
                                    `Delete (${selectedItems.length})`
                                  )}
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={handleThreeDotsClick}
                              style={{ fontSize: '12px' }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>}
                    {/* Dynamic List */}
                    <div
                      style={{
                        flex: "1 1 auto",
                        // Dynamic height based on number of items
                        // Each item is approximately 120px, plus padding
                        minHeight: filteredListings.length === 0 ? "300px" : `${Math.min(filteredListings.length * 120, 800)}px`,
                        maxHeight: "800px", // Maximum height before scrolling
                        overflowY: "auto",
                        transition: "min-height 0.3s ease"
                      }}
                    >
                      {
                        linkListings.length === 0 ? (
                           <div className="d-flex justify-content-center align-items-center flex-column text-center" style={{ height: '100%', minHeight: '400px', padding: '3rem 2rem' }}>
  {/* Headline */}
  <h2 className="mb-3" style={{ 
    fontSize: '2.5rem', 
    fontWeight: '700', 
    letterSpacing: '-0.025em',
    lineHeight: '1.1',
    maxWidth: '600px'
  }}>
    <span style={{ color: '#000000' }}>Capture. Format.</span>
    <br />
    <span style={{ color: '#A0A0A0', fontWeight: '600' }}>Share in seconds.</span>
  </h2>

  {/* Description */}
  <p className="mb-4" style={{ 
    fontSize: '1.125rem', 
    color: '#6B7280',
    maxWidth: '600px',
    lineHeight: '1.6'
  }}>
    Install our browser extension to start transforming your ChatGPT, Claude, and Gemini conversations into professional documents and presentations.
  </p>

  {/* Buttons */}
  <div className="d-flex gap-3 mb-4">
    <a
      href="https://chromewebstore.google.com/detail/threadshare/mkdemnaiienmhffacabkpboljfcdmfel"
      target="_blank"
      rel="noopener noreferrer"
      className="d-flex align-items-center justify-content-center gap-2"
      style={{ 
        textDecoration: 'none',
        backgroundColor: '#18181B',
        color: 'white',
        padding: '1rem 1.5rem',
        borderRadius: '1rem',
        fontWeight: '700',
        fontSize: '1rem',
        border: 'none',
        transition: 'all 0.3s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#27272A';
        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#18181B';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Chrome size={20} />
      Add to Chrome
    </a>
    <a
      href="https://apps.apple.com/us/app/threadshare-for-safari/id6757684487?mt=12"
      target="_blank"
      rel="noopener noreferrer"
      className="d-flex align-items-center justify-content-center gap-2"
      style={{ 
        textDecoration: 'none',
        backgroundColor: '#2563EB',
        color: 'white',
        padding: '1rem 1.5rem',
        borderRadius: '1rem',
        fontWeight: '700',
        fontSize: '1rem',
        border: 'none',
        transition: 'all 0.3s',
        boxShadow: '0 1px 3px rgba(37,99,235,0.3)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#1D4ED8';
        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(37,99,235,0.2), 0 10px 10px -5px rgba(37,99,235,0.1)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#2563EB';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(37,99,235,0.3)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Compass size={20} />
      Add to Safari
    </a>
  </div>
</div>

                        ) : filteredListings.length === 0 && searchTerm ? (
                          <div className="d-flex justify-content-center align-items-center flex-column text-center" style={{ height: '100%', minHeight: '200px' }}>
                            <div className="text-muted mb-2" style={{ fontSize: '2rem' }}>🔍</div>
                            <p className="text-muted mb-0">No results found for "{searchTerm}"</p>
                            <p className="text-muted small">Try adjusting your search terms</p>
                          </div>
                        ) :      
                      (<ListGroup variant="flush">
                        {filteredListings.map((listing, index) => {
                          // Generate a color based on the listing index/id for consistent coloring
                          const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
                          const iconColor = colors[index % colors.length];
                          const isProcessing = listing.status === 'processing';
                          const isFailed = listing.status === 'failed';
                          const isIncomplete = isProcessing || isFailed;
                          const titleValue = listing.linkTitle || listing.slug || 'Untitled link';
                          const descriptionValue = listing.shortSummaryDescription || listing.caption || 'No description available';
                          const statusLabel = isProcessing ? 'Processing' : isFailed ? 'Failed' : null;
                          const statusStyles = isProcessing
                            ? { backgroundColor: '#FEF3C7', color: '#92400E' }
                            : { backgroundColor: '#FEE2E2', color: '#991B1B' };
                          
                          return (
                          <ListGroup.Item
  key={index}
  onClick={(e) => {
    if (isSelectionMode) {
      e.preventDefault();
      handleItemSelection(listing.id);
    } else {
      if (isIncomplete) {
        e.preventDefault();
        return;
      }
      // Select this link for analytics instead of navigating
      e.preventDefault();
      handleLinkSelectionForAnalytics(listing);
    }
  }}
  onContextMenu={(e) => {
    // Right-click to select for analytics without navigating
    if (!isSelectionMode && !isIncomplete) {
      e.preventDefault();
      handleLinkSelectionForAnalytics(listing);
    }
  }}
  style={{ 
    cursor: "pointer",
    backgroundColor: isSelectionMode && selectedItems.includes(listing.id) ? '#e3f2fd' : 
                     selectedLinkForAnalytics?.id === listing.id ? '#e5f3ff' : 'transparent',
    border: selectedLinkForAnalytics?.id === listing.id ? '1px solid #b3d9ff' : 'none',
    borderRadius: selectedLinkForAnalytics?.id === listing.id ? '8px' : '0',
    padding: '1rem 1.25rem',
    transition: 'all 0.2s ease',
    boxShadow: selectedLinkForAnalytics?.id === listing.id ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'none'
  }}
  className="d-flex flex-column listing-item position-relative"
>
  {/* Selection Checkbox (Left side when in selection mode) */}
  {isSelectionMode && (
    <div className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}>
      <Form.Check
        type="checkbox"
        checked={selectedItems.includes(listing.id)}
        onChange={() => handleItemSelection(listing.id)}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '18px',
          height: '18px'
        }}
      />
    </div>
  )}

  <div className="d-flex align-items-start justify-content-between" style={{ marginLeft: isSelectionMode ? '40px' : '0' }}>
    <div className="d-flex align-items-start gap-3 flex-grow-1">
      {/* Color Indicator Circle */}
      <div 
        style={{
          width: '12px',
          height: '12px',
          backgroundColor: iconColor,
          borderRadius: '50%',
          marginTop: '4px',
          flexShrink: 0
        }}
      />
      
      <div className="flex-grow-1">
        <h6 className="mb-1" style={{ 
          fontSize: '0.95rem', 
          fontWeight: '500', 
          color: '#1a1a1a',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {isProcessing ? (
            <span className="d-inline-flex align-items-center gap-2">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              Processing link...
            </span>
          ) : isFailed ? (
            'Link processing failed'
          ) : (
            searchTerm
              ? highlightSearchTerm(titleValue.substring(0, 100), searchTerm)
              : (titleValue.substring(0, 100) + (titleValue.length > 100 ? '...' : ''))
          )}
        </h6>
        
        <div className="text-muted mb-2" style={{ 
          fontSize: '0.875rem', 
          lineHeight: '1.4',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {(() => {
            if (isProcessing) {
              return 'We are generating the summary and title. This usually takes under a minute.';
            }
            if (isFailed) {
              const errorText = listing.processingError || 'Processing failed. Please retry or recreate the link.';
              return errorText.length > 150 ? `${errorText.substring(0, 150)}...` : errorText;
            }
            const truncatedDescription = descriptionValue.length > 150
              ? descriptionValue.substring(0, 150) + '...'
              : descriptionValue;
            return searchTerm ? highlightSearchTerm(truncatedDescription, searchTerm) : truncatedDescription;
          })()}
        </div>
        
        <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.75rem' }}>
          <span>{listing.formattedDate}</span>
          {statusLabel && (
            <span
              className="badge"
              style={{
                fontSize: '0.65rem',
                fontWeight: 600,
                padding: '4px 6px',
                borderRadius: '999px',
                ...statusStyles
              }}
            >
              {statusLabel}
            </span>
          )}
        </div>
      </div>
    </div>
    
    <div className="d-flex align-items-center gap-2">
      {/* Share Button - always visible when not in selection mode */}
      {!isSelectionMode && !isIncomplete && (
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center"
          style={{
            fontSize: '0.75rem',
            padding: '4px 8px',
            borderColor: '#e5e7eb',
            color: '#6b7280',
            backgroundColor: 'transparent'
          }}
          onClick={(e) => handleShareLink(listing, e)}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#d1d5db';
            e.target.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.backgroundColor = 'transparent';
          }}
          title="Copy link"
        >
          <FaShare size={10} className="me-1" />
          Share
        </Button>
      )}

      {/* Retry Button - show when processing failed */}
      {!isSelectionMode && isFailed && (
        <Button
          variant="outline-danger"
          size="sm"
          className="d-flex align-items-center"
          style={{
            fontSize: '0.75rem',
            padding: '4px 8px',
            borderColor: '#fecaca',
            color: '#b91c1c',
            backgroundColor: 'transparent'
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleRedirect();
          }}
          title="Retry link creation"
        >
          Retry
        </Button>
      )}
      
      {/* Visit Button - appears when this link is selected for analytics */}
      {selectedLinkForAnalytics?.id === listing.id && !isSelectionMode && (
        <Button
          variant="outline-secondary"
          size="sm"
          className="d-flex align-items-center"
          style={{
            fontSize: '0.75rem',
            padding: '4px 8px',
            borderColor: '#e5e7eb',
            color: '#6b7280',
            backgroundColor: 'transparent'
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Get environment-appropriate URL (localhost in dev, production URL otherwise)
            const targetUrl = EnvironmentUtils.getEnvironmentUrl(listing.shortUrl);
            
            if (targetUrl) {
              window.open(targetUrl, '_blank');
              console.log(`🌍 Navigating to ${EnvironmentUtils.isDevelopment() ? 'localhost' : 'production'} URL:`, targetUrl);
              
              // Log environment info for debugging
              EnvironmentUtils.logEnvironmentInfo();
            } else {
              console.warn('No short URL available for this link');
            }
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#d1d5db';
            e.target.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.backgroundColor = 'transparent';
          }}
          title="Visit link"
        >
          <FaExternalLinkAlt size={10} className="me-1" />
          Visit
        </Button>
      )}
    </div>
  </div>
</ListGroup.Item>
                        );})}
                      </ListGroup>)
                      }
                       <PostPreviewModal
        show={showModal}
        onHide={() => setShowModal(false)}
        listing={selectedListing}
      />
      
      {/* Edit Preview Modal */}
      <EditPreviewModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingItem(null);
        }}
        linkData={editingItem}
        onSave={handleSaveEdit}
      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </Col>}
            {activeMenu === "Payment" && <Col md={9} className="main-content"> <PaymentScreen />  </Col>}
            {activeMenu === "Settings" && <Col md={9} className="main-content"> <SettingsPage /></Col>}
        

        {/* Analytics Panel */}
        {activeMenu === "Dashboard" && <Col md={3} className="analytics-panel">
          <Container>
            <Row>
              <Col>
                {linkListings.length === 0 ? <div></div> : <Card className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0">Analytics</h5>
                  </div>
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {getAnalyticsDataForLink(
                      // Get the selected link with the most up-to-date analytics data
                      selectedLinkForAnalytics ? 
                        linkListings.find(link => link.id === selectedLinkForAnalytics.id) || selectedLinkForAnalytics 
                        : null
                    ).map((item, index) => {
                      // Get the selected link's color for progress bars
                      const selectedLinkColor = getLinkColor(selectedLinkForAnalytics?.id, linkListings);
                      
                      return (
                      <div key={index} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <span style={{ fontSize: '0.9rem' }}>{item.label}</span>
                          <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>{item.value}</span>
                        </div>
                        <div 
                          style={{
                            width: '100%',
                            height: '5px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '2.5px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              width: `${item.progress}%`,
                              height: '100%',
                              backgroundColor: item.progress === 0 ? '#6c757d' : selectedLinkColor,
                              borderRadius: '2.5px',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        </div>
                      </div>
                    );})}
                  </div>
                  {selectedLinkForAnalytics && (
                    <Button 
                      variant="dark" 
                      className="w-100 mt-3"
                      onClick={handleRefreshAnalytics}
                      disabled={isRefreshingAnalytics}
                    >
                      <FaSync className={`me-2 ${isRefreshingAnalytics ? 'fa-spin' : ''}`} />
                      {isRefreshingAnalytics ? 'Refreshing...' : 'Refresh Analytics'}
                    </Button>
                  )}
                  {!selectedLinkForAnalytics && (
                    <Button 
                      variant="outline-secondary" 
                      className="w-100 mt-3"
                      disabled
                    >
                      Select a Link to View Analytics
                    </Button>
                  )}
                </Card>}

                {linkListings.length === 0 ? <div></div> : (
                <Card style={{
                  border: '1px solid #f1f3f4',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  marginTop: '16px'
                }} className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.5rem' }}>
                        <path d="M10 13C10.7956 13 11.5587 13.3161 12.1213 13.8787C12.6839 14.4413 13 15.2044 13 16C13 16.7956 12.6839 17.5587 12.1213 18.1213C11.5587 18.6839 10.7956 19 10 19H6C5.20435 19 4.44129 18.6839 3.87868 18.1213C3.31607 17.5587 3 16.7956 3 16C3 15.2044 3.31607 14.4413 3.87868 13.8787C4.44129 13.3161 5.20435 13 6 13H10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 11C13.2044 11 12.4413 10.6839 11.8787 10.1213C11.3161 9.55871 11 8.79565 11 8C11 7.20435 11.3161 6.44129 11.8787 5.87868C12.4413 5.31607 13.2044 5 14 5H18C18.7956 5 19.5587 5.31607 20.1213 5.87868C20.6839 6.44129 21 7.20435 21 8C21 8.79565 20.6839 9.55871 20.1213 10.1213C19.5587 10.6839 18.7956 11 18 11H14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 16L16 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <h2 className="mb-0" style={{ 
                        fontSize: '1.5rem', 
                        fontWeight: '600', 
                        color: '#1a1a1a',
fontFamily: 'var(--font-family-base)',
                        marginRight: '0.75rem'
                      }}>Impressions</h2>
                      {selectedLinkForAnalytics && (
                        <span className="badge" style={{
                          backgroundColor: '#e8f5e8',
                          color: '#2d5a2d',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: 'none'
                        }}>
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                      {selectedLinkForAnalytics ? (
                        <p className="text-muted mb-0" style={{ 
                          fontSize: '0.875rem',
                          fontFamily: 'var(--font-family-base)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '250px'
                        }}>
                          {((selectedLinkForAnalytics.linkTitle || selectedLinkForAnalytics.slug).length > 40 
                            ? (selectedLinkForAnalytics.linkTitle || selectedLinkForAnalytics.slug).substring(0, 40) + '...' 
                            : (selectedLinkForAnalytics.linkTitle || selectedLinkForAnalytics.slug))} • {selectedLinkForAnalytics.formattedDate}
                        </p>
                      ) : (
                        <p className="text-muted mb-0" style={{ 
                          fontSize: '0.875rem',
fontFamily: 'var(--font-family-base)'
                        }}>
                          Select a link to view its impression performance throughout the week
                        </p>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      {selectedLinkForAnalytics && (
                        <div 
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#e8f5e8',
                            borderRadius: '50%',
                            color: '#2d5a2d',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {getDisplayCount()}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedLinkForAnalytics && (
                    <div className="mt-3 mb-2">
                      {isLoadingImpressions ? (
                        <div className="text-center py-4">
                          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            Loading impression data...
                          </p>
                        </div>
                      ) : impressionsData && impressionsData.dailyData && impressionsData.dailyData.some(day => day.count > 0) ? (
                        <BarChart 
                          onDaySelect={handleDaySelection} 
                          selectedDayIndex={selectedDay?.index !== undefined ? selectedDay.index : null}
                          impressionsData={impressionsData}
                          loading={isLoadingImpressions}
                        />
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            No impressions in last 7 days
                          </p>
                          <p className="text-muted mb-0" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                            (Shows recent activity from 30-day range)
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>)}

                {/* Remaining Links Card - moved below impressions */}
                {linkListings.length > 0 && (
                  <RemainingLinksCard 
                    style={{ marginTop: '16px', marginBottom: '16px' }} 
                    onUpgradeClick={() => {
                      setUpgradeModalTrigger('button');
                      setShowUpgradeModal(true);
                    }}
                  />
                )}

                <Card
      className="p-4 text-center"
      style={{
        marginTop: "40px",
        position: "relative"
      }}
    >
      {/* Left Image */}
      <img
        src={referIllLeft} 
        alt="Left Clip Art"
        style={{
          position: "absolute",
          top: "-20px",
          left: "20px",
          height: "80px",
        }}
      />

      {/* Right Image */}
      <img
        src={referIllRight} 
        alt="Right Clip Art"
        style={{
          position: "absolute",
          top: "-20px",
          right: "20px",
          height: "80px",
        }}
      />

      {/* Text Content */}
      <h5 className="mb-3" style={{ fontWeight: "bold" , marginTop: "60px", textAlign: "left"}}>
        Share {WEBSITE_NAME}
      </h5>
      <p className="text-muted mb-4">
        Love using {WEBSITE_NAME}? Help your friends discover a better way to organize and track their AI conversations.
      </p>

      {/* Button */}
      <Button
        variant="dark"
        style={{
          borderRadius: "8px",
          padding: "10px 20px",
          fontWeight: "bold",
        }}
        onClick={async () => {
          const referralData = {
            title: SHARING_TITLE,
            text: SHARING_DESCRIPTION,
            url: WEBSITE_URL
          };
          
          try {
            // Check if Web Share API is supported
            if (navigator.share && navigator.canShare && navigator.canShare(referralData)) {
              await navigator.share(referralData);
              console.log('✅ Shared successfully via Web Share API');
            } else if (navigator.share) {
              // Fallback for browsers that support share but not canShare
              await navigator.share(referralData);
              console.log('✅ Shared successfully via Web Share API (fallback)');
            } else {
              // Fallback for browsers without Web Share API
              await navigator.clipboard.writeText(referralData.url);
              alert('🔗 Referral link copied to clipboard!\n\nShare this link: ' + referralData.url);
              console.log('📋 Copied to clipboard as fallback');
            }
          } catch (error) {
            // Handle user cancellation or other errors
            if (error.name === 'AbortError') {
              console.log('📤 User cancelled sharing');
            } else {
              console.error('❌ Error sharing:', error);
              // Fallback to clipboard
              try {
                await navigator.clipboard.writeText(referralData.url);
                alert('🔗 Referral link copied to clipboard!\n\nShare this link: ' + referralData.url);
              } catch (clipboardError) {
                console.error('❌ Clipboard fallback failed:', clipboardError);
                alert('Share ' + WEBSITE_NAME + ' with this link: ' + referralData.url);
              }
            }
          }
        }}
      >
        Share with Friends
      </Button>
    </Card>
              </Col>
            </Row>
          </Container>
        </Col>}
        
      </Row>

      {/* Clipboard Success Banner */}
      {showClipboardBanner && (
        <div 
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: clipboardMessage.includes('✅') ? '#22c55e' : '#ef4444',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            animation: 'slideUp 0.3s ease-out',
            maxWidth: '90vw',
            textAlign: 'center'
          }}
        >
          {clipboardMessage}
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger={upgradeModalTrigger}
      />

      {/* Upgrade Celebration Modal */}
      <UpgradeCelebrationModal 
        isOpen={showUpgradeCelebration}
        onClose={closeUpgradeCelebration}
        planName={userStatus === 'pro' ? 'Pro' : 'Starter'}
      />

    </Container>
  );
};

// Remove the wrapper since providers are now at root level
export default DashScreen;
